import { NEW_ENTITY_VALUE } from "../../helpers/mechanisms/buildAcceptedPayload";
import { mechanismCategoryLabels } from "../../helpers/dataHelpers/mechanismCategories";

/**
 * @typedef {object} MechanismRow
 * @property {string} label - Concrete mechanism name (editable for new ones)
 * @property {string} category - Taxonomy key
 * @property {string|null} mechanismId - Existing TocMechanisms ID (context-adding row) or null
 * @property {string} evidence - Literal quote from the interview
 * @property {string} explanation - Why the quote shows this mechanism
 * @property {string} targetgroupId - Selected targetgroup ID, "" or NEW_ENTITY_VALUE
 * @property {string} newTargetgroupLabel - Proposed new targetgroup label (standalone tenants)
 * @property {string} activityId - Selected activity ID, "" or NEW_ENTITY_VALUE
 * @property {string} newActivityLabel - Proposed new activity label (standalone tenants)
 * @property {string} effectId - Selected MSI-effect ID or ""
 * @property {boolean} isNew - Whether this is a new mechanism vs an existing one
 * @property {boolean} accepted - Whether the row is included in the save
 */

/**
 * Presentational list of Decca's proposed werkzame mechanismen. State is owned
 * by the parent (review screen or the interview-detail unified review), which
 * passes the rows and an updater. No Firestore, no save button here.
 *
 * @param {object} props
 * @param {MechanismRow[]} props.rows - Editable proposal rows
 * @param {(index: number, patch: object) => void} props.updateRow - Patches one row
 * @param {Array<{ID: string, Organisation: string}>} props.targetgroups - Tenant targetgroups for the dropdown
 * @param {Array<{ID: string, Activity: string}>} props.activities - Tenant activities for the dropdown
 * @param {Array<{ID: string, Effect: string}>} props.msiEffects - Tenant MSI effects for the dropdown
 */
const MechanismProposalList = ({ rows, updateRow, targetgroups, activities, msiEffects }) => (
    <div className="mechanism-review-list">
        {rows.map((r, index) => (
            <div
                className={
                    r.accepted
                        ? "mechanism-review-row"
                        : "mechanism-review-row mechanism-review-row-excluded"
                }
                key={index}
            >
                <div className="mechanism-review-row-main">
                    {r.mechanismId ? (
                        // Context for an existing mechanism — the title is the
                        // curated TOC entry, not editable here.
                        <span className="mechanism-review-label-static">{r.label}</span>
                    ) : (
                        <input
                            className="mechanism-review-label"
                            type="text"
                            value={r.label}
                            onChange={(e) => updateRow(index, { label: e.target.value })}
                        />
                    )}
                    {r.category && (
                        <span className="mechanism-review-category">
                            {mechanismCategoryLabels[r.category] || r.category}
                        </span>
                    )}
                    {r.isNew ? (
                        <span className="mechanism-review-badge mechanism-review-badge-new">
                            nieuw
                        </span>
                    ) : (
                        <span className="mechanism-review-badge">bestaand</span>
                    )}
                </div>

                <blockquote className="mechanism-review-evidence">{r.evidence}</blockquote>
                {r.explanation && (
                    <p className="mechanism-review-explanation">{r.explanation}</p>
                )}

                <div className="mechanism-review-links">
                    <label className="mechanism-review-link">
                        <span>Doelgroep</span>
                        <select
                            value={r.targetgroupId}
                            onChange={(e) => updateRow(index, { targetgroupId: e.target.value })}
                        >
                            <option value="">Geen specifieke doelgroep</option>
                            {r.newTargetgroupLabel && (
                                <option value={NEW_ENTITY_VALUE}>
                                    ➕ Nieuwe doelgroep: {r.newTargetgroupLabel}
                                </option>
                            )}
                            {targetgroups.map((targetgroup) => (
                                <option key={targetgroup.ID} value={targetgroup.ID}>
                                    {targetgroup.Organisation}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="mechanism-review-link">
                        <span>Activiteit</span>
                        <select
                            value={r.activityId}
                            onChange={(e) => updateRow(index, { activityId: e.target.value })}
                        >
                            <option value="">Organisatiebreed (geen activiteit)</option>
                            {r.newActivityLabel && (
                                <option value={NEW_ENTITY_VALUE}>
                                    ➕ Nieuwe activiteit: {r.newActivityLabel}
                                </option>
                            )}
                            {activities.map((a) => (
                                <option key={a.ID} value={a.ID}>
                                    {a.Activity}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="mechanism-review-link">
                        <span>Effect</span>
                        <select
                            value={r.effectId}
                            onChange={(e) => updateRow(index, { effectId: e.target.value })}
                        >
                            <option value="">Geen MSI-effect</option>
                            {msiEffects.map((eff) => (
                                <option key={eff.ID} value={eff.ID}>
                                    {eff.Effect}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>

                {/* Label is the ACTION the click performs, not the row state —
                    the row itself shows state via -excluded dimming. */}
                <button
                    className={
                        r.accepted
                            ? "mechanism-review-toggle"
                            : "mechanism-review-toggle mechanism-review-toggle-active"
                    }
                    onClick={() => updateRow(index, { accepted: !r.accepted })}
                >
                    {r.accepted ? "Overslaan" : "Meenemen"}
                </button>
            </div>
        ))}
    </div>
);

export default MechanismProposalList;
